// Tenancy feature flags.
//
// Each entry is a toggle the portal can flip per tenancy. Components import
// `useFeature(id)` (or read FEATURES directly for module-level decisions)
// and gate UI accordingly, never inline-hardcode `if (industry...)` checks.
//
// The prototype stores overrides in localStorage so the in-app toggle in
// the topbar persists across reloads. The real implementation will read
// these from the tenancy settings API; the surface here matches what the
// portal Features section will expose.
//
// Postgres mapping:
//   - per-tenancy table `tenancy_features` (tenancy_id, flag_id, enabled,
//     updated_at, updated_by) keyed by FEATURE_DEFAULTS keys.
//   - feature flags should be tenancy-scoped, never per-user. localStorage
//     here is prototype-only; production loads via the existing
//     `tenancy.service.js` slice on app boot.
//   - lookup metadata (FEATURE_META label + description) belongs in code,
//     not the database, since it is product copy.
//
// New data points:
//   - per-flag audit log (who flipped it, when) for SOC compliance.
//   - flag entitlements: which subscription tier each flag requires.
//
// Integration hook:
//   - extend `tenancy.service.js` with `useGetTenancyFeaturesQuery()` and
//     `useUpdateFeatureMutation`; cache TTL 5 minutes since flags rarely
//     change. Replace this localStorage layer with a thin Redux selector
//     over the tenancy slice once the API is in place.

const STORAGE_KEY = 'rbx-feature-flags';
// Per-widget entitlements ("<page>:<widgetId>" -> false when switched off).
// Cached here only so the synchronous reads below work before the API
// responds; /dashboard/features is the source of truth.
const WIDGET_STORAGE_KEY = 'rbx-widget-entitlements';
const TIER_STORAGE_KEY = 'rbx-licence-tier';

// Licence tier sits above the individual flag toggles - Essentials is the
// entry-level SKU and excludes industry benchmarking; Enhanced unlocks the
// full surface. Individual flag overrides are still respected on top of the
// tier (e.g. an Enhanced tenancy can still switch DWM off), but flags that
// the current tier does not entitle are forced off regardless of override.
export const LICENCE_TIERS = {
    essentials: { label: 'Essentials', excludes: ['industryBenchmarking'] },
    enhanced:   { label: 'Enhanced',   excludes: [] },
};
export const LICENCE_TIER_DEFAULT = 'enhanced';

// Defaults match what the platform can actually serve with real data:
//   - industryBenchmarking OFF: no tenancy industry assignment exists
//     anywhere on the platform, so there is no industry cohort to compare
//     against. Platform-wide anchors (from the dashboard benchmark
//     aggregation) render in the off-state copy instead.
//   - darkWebMonitoring OFF: no breach-feed integration exists; the page
//     shows its designed feature-off notice.
// Both can still be force-enabled via the localStorage override for demos.
export const FEATURE_DEFAULTS = {
    industryBenchmarking: false,   // No industry data source on the platform
    darkWebMonitoring:    false,   // No breach-feed data source on the platform
    flaggedSimulations:   true,    // Derived from real click/report rates
    exportBuilder:        true,    // Export Builder page (exports real data)
};

export const FEATURE_META = {
    industryBenchmarking: {
        label: 'Industry benchmarking',
        description: 'Compare your org to anonymised peer-org averages on every risk-score factor. When off, all "vs industry" anchors and the top-25% pill are hidden.',
    },
    darkWebMonitoring: {
        label: 'Dark web monitoring',
        description: 'Continuous scan of breach corpora for emails belonging to your users.',
    },
    flaggedSimulations: {
        label: 'Flagged simulations',
        description: 'Dedicated page listing sims that hit a flag criterion, with remediation guidance.',
    },
    exportBuilder: {
        label: 'Export builder',
        description: 'Tick-box assembly of custom exports drawn from any page in the product.',
    },
};

function loadOverrides() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
    catch { return {}; }
}
function saveOverrides(overrides) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides)); } catch { /* ignore */ }
}

function loadWidgetEntitlements() {
    try { return JSON.parse(localStorage.getItem(WIDGET_STORAGE_KEY)) || {}; }
    catch { return {}; }
}
function saveWidgetEntitlements(map) {
    try { localStorage.setItem(WIDGET_STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

function loadTier() {
    try {
        const v = localStorage.getItem(TIER_STORAGE_KEY);
        return v && LICENCE_TIERS[v] ? v : LICENCE_TIER_DEFAULT;
    } catch { return LICENCE_TIER_DEFAULT; }
}
function saveTier(tier) {
    try { localStorage.setItem(TIER_STORAGE_KEY, tier); } catch { /* ignore */ }
}

// Live feature snapshot for module-level reads (e.g. inside data files).
// Components should prefer `useFeature(id)` so they re-render on toggle.
let _overrides = loadOverrides();
let _widgetEntitlements = loadWidgetEntitlements();
let _tier = loadTier();

function tierExcludes(key) {
    return (LICENCE_TIERS[_tier]?.excludes || []).includes(key);
}

export const FEATURES = new Proxy({}, {
    get(_target, key) {
        if (tierExcludes(key)) return false;
        if (key in _overrides) return _overrides[key];
        return FEATURE_DEFAULTS[key];
    },
});

// Tiny pub-sub so the React layer can re-render when a flag changes
// without a full app reload.
const listeners = new Set();
export function subscribeFeatures(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export function setFeature(id, value) {
    _overrides = { ..._overrides, [id]: value };
    saveOverrides(_overrides);
    listeners.forEach((fn) => fn());
}

// Seed the in-memory overrides from the backend (reportsBFeatures.service ->
// /dashboard/features). Called once on app boot with the tenancy-resolved flags so the
// synchronous FEATURES proxy stays correct across machines/admins. localStorage is kept
// only as an offline cache; the API is the source of truth. Persisting a toggle is done
// via the saveReportsBFeatures mutation (see the Features page), which also calls setFeature
// for the instant local echo.
export function hydrateOverrides(apiFlags) {
    if (!apiFlags || typeof apiFlags !== 'object') return;
    _overrides = { ...apiFlags };
    saveOverrides(_overrides);
    listeners.forEach((fn) => fn());
}

// ---- Widget entitlements -------------------------------------------------
// A second, separate axis from the page-level flags above: which individual
// widgets a tenancy is licensed for. Entitlements are set by super admins on
// the Features page and stored on the admin-gated /dashboard/features record.
// They are NOT user configuration: the in-report Customise drawer and the
// export builder read them but cannot change them, so a tenancy admin can
// never restore a widget a super admin has switched off. Customise still owns
// the separate "hidden" list, which only ever hides entitled widgets.
export function widgetKey(page, widgetId) { return `${page}:${widgetId}`; }

// Absent means entitled - only explicit "off" decisions are stored.
export function isWidgetEntitled(page, widgetId) {
    return _widgetEntitlements[widgetKey(page, widgetId)] !== false;
}

export function setWidgetEntitled(page, widgetId, value) {
    _widgetEntitlements = { ..._widgetEntitlements, [widgetKey(page, widgetId)]: !!value };
    saveWidgetEntitlements(_widgetEntitlements);
    listeners.forEach((fn) => fn());
}

// Seed from the backend on boot (same record as hydrateOverrides).
export function hydrateWidgetEntitlements(apiWidgets) {
    if (!apiWidgets || typeof apiWidgets !== 'object') return;
    _widgetEntitlements = { ...apiWidgets };
    saveWidgetEntitlements(_widgetEntitlements);
    listeners.forEach((fn) => fn());
}

// Full map for the PUT body (the features record is saved wholesale).
export function currentWidgetEntitlements() { return { ..._widgetEntitlements }; }

export function resetWidgetEntitlements() {
    _widgetEntitlements = {};
    saveWidgetEntitlements(_widgetEntitlements);
    listeners.forEach((fn) => fn());
}

export function resetFeatures() {
    _overrides = {};
    saveOverrides(_overrides);
    listeners.forEach((fn) => fn());
}

export function getLicenceTier() { return _tier; }
export function setLicenceTier(tier) {
    if (!LICENCE_TIERS[tier]) return;
    _tier = tier;
    saveTier(tier);
    listeners.forEach((fn) => fn());
}
export function isFeatureLockedByTier(id) { return tierExcludes(id); }

export function currentFeatures() {
    const out = {};
    for (const k of Object.keys(FEATURE_DEFAULTS)) out[k] = FEATURES[k];
    return out;
}
